import { Button, Dropdown, Flex, Input, Skeleton, Space, Typography, Switch, Divider, Avatar, Tooltip, Select, Tag, Tour } from "antd"
import { useCrewQuery, useDuplicateRouteMutation, useRouteSchedulesQuery, useRoutingDataQuery, useJobsForCreateRouteQuery } from "../routingQueries"
import VSpace from "../../common/VSpace"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan, faCopy, faEllipsisV, faMap, faTrash, faSort } from "@fortawesome/pro-solid-svg-icons"
import { CSSProperties, useEffect, useReducer, useState } from "react"
import dayjs from "dayjs"
import { getFilteredRoutes, reducer, RoutingListFilters, defaultRoutingListFilters } from "../routingModel"
import DeleteRouteModal from "../RouteDelete/DeleteRouteModal"
import CreateRouteModal from "../RouteAdd/CreateRouteModal"
import ActivateRouteModal from "./ActivateRouteModal"
import InactivateRouteModal from "./InactivateRouteModal"
import { Route, RoutingType } from "../routingApi"
import { red } from "@ant-design/colors"
import WelcomeToNewRoutingModal from "../WelcomeToNewRoutingModal"
import AddJobsByMapPreRender from "../RouteAdd/AddJobsByMap/AddJobsByMapPreRender"
import { useTenantLocationsDataQuery } from '../../../domain/tenantLocations/tenantLocationsQueries'
import { TenantLocationType } from '../../../domain/tenantLocations/tenantLocation'
import { DEFAULT_MAP_ZOOM } from '../RouteAdd/routeAddModel'
const { Text } = Typography

export const routeCardStyle: CSSProperties = {
  border: '1px solid #e9e9e9',
  borderRadius: 4,
  padding: 16,
  color: '#1e1e1e',
  marginBottom: '10px',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
}

const routeCardClassName = 'route-card-hover'

export type Props = {
  onViewRoute: (routeId: number, newRoute: boolean) => void
}

const RouteList = ({ onViewRoute }: Props) => {
  const [filters, setFilters] = useState<RoutingListFilters>(defaultRoutingListFilters)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false)
  const [isInactivateModalOpen, setIsInactivateModalOpen] = useState(false)
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null)
  const [state, dispatch] = useReducer(reducer, [])
  const [createRouteModalOpening, setCreateRouteModalOpening] = useState(false)
  const [shouldPreRenderAddJobsMap, setShouldPreRenderAddJobsMap] = useState(false)

  const welcomeViewed = window.localStorage.getItem('welcome-to-new-routing-modal') === 'true'
  const viewed = window.localStorage.getItem('route-list-tour-viewed') === 'true'
  const [isTourOpen, setIsTourOpen] = useState(!viewed && welcomeViewed)

  const duplicateRouteMutation = useDuplicateRouteMutation()
  const { data, isLoading } = useRoutingDataQuery()
  const { data: crew } = useCrewQuery()
  const { data: routeSchedules } = useRouteSchedulesQuery()

  // Pre-load jobs data for AddJobsByMap
  const { data: jobsForCreateRoute } = useJobsForCreateRouteQuery()
  const { data: tenantLocations } = useTenantLocationsDataQuery()
  const shopLocation = tenantLocations?.find(tl => tl.type === TenantLocationType.Shop)

  useEffect(() => {
    dispatch({ type: 'ROUTES_LOADED', value: getFilteredRoutes(data || [], filters, crew) || [] })
  }, [data, crew])

  // Pre-render AddJobsByMap when data is ready
  useEffect(() => {
    if (jobsForCreateRoute && shopLocation && !shouldPreRenderAddJobsMap && !isLoading) {
      // Delay pre-render to avoid blocking route list loading
      const timeout = setTimeout(() => {
        setShouldPreRenderAddJobsMap(true)
      }, 3000) // 3 second delay to ensure route list is fully loaded

      return () => clearTimeout(timeout)
    }
  }, [jobsForCreateRoute, shopLocation, shouldPreRenderAddJobsMap, isLoading])

  useEffect(() => {
    dispatch({ type: 'ROUTES_FILTERED', value: getFilteredRoutes(data || [], filters, crew) || [] })
  }, [filters, data, crew])

  const handleDeleteClick = (route: Route) => {
    setSelectedRoute(route)
    setIsDeleteModalOpen(true)
  }

  const handleToggleActive = (route: Route, newActiveState: boolean) => {
    setSelectedRoute(route)
    if (newActiveState) {
      setIsActivateModalOpen(true)
    } else {
      setIsInactivateModalOpen(true)
    }
  }

  const tourSteps = [
    {
      title: 'Create New Route',
      description: 'Click here to create a new route. You can add jobs, assign crew members, and plan your optimal driving route all in one place.',
      target: () => document.getElementById('create-new-route-button') as HTMLElement || null,
    },
  ]

  return <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
        <Input.Search
          placeholder="Search by route name"
          value={filters.searchTerm}
          onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
          style={{ flex: 1 }}
        />
        <Divider type="vertical" style={{ height: '24px', margin: '0 12px' }} />
        <Space>
          <Text strong style={{ marginLeft: 0 }}>Route Type</Text>
          <Select
            placeholder="Filter by type"
            style={{ minWidth: 150 }}
            value={filters.routeType}
            onChange={(value) => setFilters({ ...filters, routeType: value })}
            options={[
              { label: 'Planned', value: RoutingType.Planned },
              { label: 'On Demand', value: RoutingType.OnDemand }
            ]}
            allowClear
          />
          <Text strong style={{ marginLeft: 12 }}>Crew Members</Text>
          <Select
            mode="multiple"
            placeholder="Filter by crew"
            style={{ minWidth: 200, marginLeft: 12 }}
            value={filters.crewMemberIds}
            onChange={(values) => setFilters({ ...filters, crewMemberIds: values })}
            options={crew?.map(member => ({
              label: member.name,
              value: member.id.toString()
            }))}
            allowClear
            maxTagCount={2}
            optionFilterProp='label'
          />
          <Divider type="vertical" style={{ height: '24px', margin: '0 12px' }} />
          <Switch
            checked={filters.active}
            onChange={(checked) => setFilters({ ...filters, active: checked })}
          />
          <Text strong>Hide Inactive</Text>
        </Space>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Divider type="vertical" style={{ height: '24px', margin: '0 24px' }} />
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'createDateDesc',
                label: <div>
                  <strong>Create Date</strong>
                  <div style={{ fontSize: 12, color: filters.sortField === 'createdAtUtc' && filters.sortDirection === 'desc' ? '#fa541c' : '#888' }}>
                    Newest to Oldest
                  </div>
                </div>,
                onClick: () => setFilters({ ...filters, sortField: 'createdAtUtc', sortDirection: 'desc' }),
                style: filters.sortField === 'createdAtUtc' && filters.sortDirection === 'desc' ? { background: '#fff7e6' } : {},
              },
              {
                key: 'createDateAsc',
                label: <div>
                  <span style={{ fontWeight: 500 }}>Create Date</span>
                  <div style={{ fontSize: 12, color: filters.sortField === 'createdAtUtc' && filters.sortDirection === 'asc' ? '#fa541c' : '#888' }}>
                    Oldest to Newest
                  </div>
                </div>,
                onClick: () => setFilters({ ...filters, sortField: 'createdAtUtc', sortDirection: 'asc' }),
                style: filters.sortField === 'createdAtUtc' && filters.sortDirection === 'asc' ? { background: '#fff7e6' } : {},
              },
              {
                key: 'crewNameAsc',
                label: <div>
                  <span style={{ fontWeight: 500 }}>Crew Member Name</span>
                  <div style={{ fontSize: 12, color: filters.sortField === 'crewMemberName' && filters.sortDirection === 'asc' ? '#fa541c' : '#888' }}>
                    A to Z
                  </div>
                </div>,
                onClick: () => setFilters({ ...filters, sortField: 'crewMemberName', sortDirection: 'asc' }),
                style: filters.sortField === 'crewMemberName' && filters.sortDirection === 'asc' ? { background: '#fff7e6' } : {},
              },
              {
                key: 'crewNameDesc',
                label: <div>
                  <span style={{ fontWeight: 500 }}>Crew Member Name</span>
                  <div style={{ fontSize: 12, color: filters.sortField === 'crewMemberName' && filters.sortDirection === 'desc' ? '#fa541c' : '#888' }}>
                    Z to A
                  </div>
                </div>,
                onClick: () => setFilters({ ...filters, sortField: 'crewMemberName', sortDirection: 'desc' }),
                style: filters.sortField === 'crewMemberName' && filters.sortDirection === 'desc' ? { background: '#fff7e6' } : {},
              },
            ]
          }}
        >
          <Button style={{ marginRight: 16 }} icon={<FontAwesomeIcon icon={faSort || faEllipsisV} />}>
            Sort
          </Button>
        </Dropdown>
        <Divider type="vertical" style={{ height: '24px', margin: '0 24px' }} />
        <Button
          id="create-new-route-button"
          type="primary"
          style={{ marginRight: 24 }}
          onClick={() => {
            setCreateRouteModalOpening(true)
            setIsCreateModalOpen(true)
            setTimeout(() => {
              setCreateRouteModalOpening(false)
            }, 1000)
          }}
        >
          + New Route
        </Button>
      </div>
    </div>
    <div style={{ marginTop: '20px' }}>
      {isLoading && <Skeleton active />}
      {!isLoading && state?.map(route => (
        <div
          key={route.id}
          style={routeCardStyle}
          className={routeCardClassName}
          onClick={() => onViewRoute(route.id, false)}>
          <Flex justify="space-between" align="center">
            <Space size="large">
              <VSpace>
                <strong style={{ fontWeight: 600 }}>{route.name}</strong>
                <Space>
                  <Tag
                    style={{
                      borderRadius: '12px',
                      ...(route.routeType === RoutingType.Planned && {
                        borderColor: '#531DAB',
                        backgroundColor: '#F9F0FF',
                        color: '#531DAB'
                      }),
                      ...(route.routeType === RoutingType.OnDemand && {
                        borderColor: '#08979C',
                        backgroundColor: '#E6FFFB',
                        color: '#08979C'
                      })
                    }}
                  >
                    {route.routeType === RoutingType.Planned ? "Planned" : "On Demand"}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: '12px' }}>|</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {route.jobsCount ? `Jobs ${route.jobsCount}` : 'Jobs 0'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>|</Text>
                  {routeSchedules && routeSchedules?.filter(s => s.routeId === route.id).length > 0 &&
                    <>
                      <Text type="secondary" style={{ fontSize: '12px' }}> {(() => {
                        const dayOfWeek = routeSchedules?.filter(s => s.routeId === route.id)[0].scheduleConfig?.dayOfWeek
                        return dayOfWeek ? dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1) : ''
                      })()}</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>|</Text>
                    </>
                  }
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <Avatar.Group
                      size="small"
                      maxCount={1}
                      maxStyle={{
                        opacity: 0.5,
                        backgroundColor: '#faad14'
                      }}
                    >
                      {!route.crewLeadId && (
                        <Tooltip title={"No Crew Lead"}>
                          <Avatar style={{ backgroundColor: 'white' }} icon={<FontAwesomeIcon icon={faBan} color={red.primary} />} />
                        </Tooltip>
                      )}
                      {route.crewMemberIds?.split(',').sort((a, b) => {
                        if (parseInt(a) === route.crewLeadId) return -1
                        if (parseInt(b) === route.crewLeadId) return 1
                        return 0
                      }).map(id =>
                        <Tooltip key={id} title={crew?.find(c => c.id === parseInt(id))?.name}>
                          <Avatar src={crew?.find(c => c.id === parseInt(id))?.avatar}> {crew?.find(c => c.id === parseInt(id))?.name.charAt(0)} </Avatar>
                        </Tooltip>
                      )}
                    </Avatar.Group>
                  </Text>
                </Space>
              </VSpace>
            </Space>
            <Space>
              <Flex gap={20}>
                <Space direction="vertical">
                  <Flex justify="flex-end">
                    <Space>
                      <Switch
                        checked={route.active}
                        onChange={(checked) => handleToggleActive(route, checked)}
                        onClick={(checked, event) => event.stopPropagation()} />
                      <Text strong>{route.active ? 'Active' : 'Inactive'}</Text>
                    </Space>
                  </Flex>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {dayjs(route.createdAtUtc).isValid() && `Created ${dayjs(route.createdAtUtc).format('MMMM D, YYYY')}`}
                    {dayjs(route.createdAtUtc).isValid() && dayjs(route.updatedAtUtc).isValid() && ' | '}
                    {dayjs(route.updatedAtUtc).isValid() && `Last Edited ${dayjs(route.updatedAtUtc).format('MMMM D, YYYY')}`}
                  </Text>
                </Space>
              </Flex>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'view',
                      label: <Space><FontAwesomeIcon icon={faMap} />View</Space>,
                      onClick: (e) => {
                        e?.domEvent?.stopPropagation()
                        onViewRoute(route.id, false)
                      },
                    },
                    {
                      key: 'duplicate',
                      label: <Space><FontAwesomeIcon icon={faCopy} />Duplicate</Space>,
                      onClick: (e) => {
                        e?.domEvent?.stopPropagation()
                        duplicateRouteMutation.mutate({ routeId: route.id })
                      }
                    },
                    {
                      key: 'delete',
                      label: <Space><FontAwesomeIcon icon={faTrash} />Delete</Space>,
                      onClick: (e) => {
                        e?.domEvent?.stopPropagation()
                        handleDeleteClick(route)
                      }
                    },
                  ]
                }}
              >
                <Button
                  type="text"
                  icon={<FontAwesomeIcon icon={faEllipsisV} />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </Space>
          </Flex>
        </div>
      ))}
    </div>
    <DeleteRouteModal
      route={selectedRoute!}
      isOpen={isDeleteModalOpen}
      onClose={() => {
        setIsDeleteModalOpen(false)
        setSelectedRoute(null)
      }}
    />
    <CreateRouteModal
      isOpen={isCreateModalOpen}
      onSave={(routeId: number) => {
        setIsCreateModalOpen(false)
        onViewRoute(routeId, true)
      }}
      onClose={() => {
        if (!createRouteModalOpening) {
          setIsCreateModalOpen(false)
        }
      }}
      crew={crew}
    />
    <ActivateRouteModal
      visible={isActivateModalOpen}
      onCancel={() => {
        setIsActivateModalOpen(false)
        setSelectedRoute(null)
      }}
      route={selectedRoute}
      onSuccess={() => {
        setIsActivateModalOpen(false)
        setSelectedRoute(null)
        window.posthog.capture('route-activated', { routeId: selectedRoute?.id })
      }}
    />
    <InactivateRouteModal
      visible={isInactivateModalOpen}
      onCancel={() => {
        setIsInactivateModalOpen(false)
        setSelectedRoute(null)
      }}
      route={selectedRoute}
      onSuccess={() => {
        setIsInactivateModalOpen(false)
        setSelectedRoute(null)
      }}
    />
    <WelcomeToNewRoutingModal
      onClose={() => setIsTourOpen(true)}
    />
    <style>
      {`
        .${routeCardClassName}:hover {
          background-color: #FFCABD
        }
      `}
    </style>

    {/* Hidden pre-render of AddJobsByMap for performance */}
    {shouldPreRenderAddJobsMap && jobsForCreateRoute && shopLocation && (
      <div style={{
        position: 'absolute',
        top: -9999,
        left: -9999,
        width: '800px',
        height: '600px',
        visibility: 'hidden',
        pointerEvents: 'none' // Prevent any interactions
      }}>
        <AddJobsByMapPreRender
          jobs={jobsForCreateRoute}
          initialMapViewState={{
            longitude: shopLocation.longitude,
            latitude: shopLocation.latitude,
            zoom: DEFAULT_MAP_ZOOM,
            bearing: 0,
            pitch: 0,
            padding: { top: 0, bottom: 0, left: 0, right: 0 }
          }}
        />
      </div>
    )}

    <Tour
      open={isTourOpen}
      closable={true}
      onClose={() => {
        setIsTourOpen(false)
        window.localStorage.setItem('route-list-tour-viewed', 'true')
      }}
      onFinish={() => {
        setIsTourOpen(false)
        window.localStorage.setItem('route-list-tour-viewed', 'true')
      }}
      steps={tourSteps.filter(step => step.target() !== null)}
      type="default"
      disabledInteraction={true}
    />
  </div>
}

export default RouteList